/**
 * ReviewFormPage — Formulario público de review accesible via token único
 * Ruta: /reviews/:token (sin auth)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Upload, X, CheckCircle, AlertCircle, Loader, Copy, Check, Tag } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function StarPicker({ value, onChange, size = 36 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          className="focus:outline-none transition-transform hover:scale-110 active:scale-95">
          <svg width={size} height={size} viewBox="0 0 24 24" fill={n <= (hovered || value || 0) ? '#f59e0b' : 'none'}
            stroke={n <= (hovered || value || 0) ? '#f59e0b' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
    </div>
  );
}

function PhotoUpload({ token, onPhotoAdded, onPhotoRemoved, photos, maxPhotos = 5 }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (photos.length >= maxPhotos) { return; }
    const file = files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND}/api/reviews/photos/upload/${token}`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onPhotoAdded(data.url);
    } catch (e) {
      alert('Error al subir la foto: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {photos.map((url, i) => {
          const fullUrl = url.startsWith('/') ? `${BACKEND}${url}` : url;
          return (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-amber-400/30">
              <img src={fullUrl} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onPhotoRemoved(i)}
                className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 hover:bg-black/80">
                <X size={10} className="text-white" />
              </button>
            </div>
          );
        })}
        {photos.length < maxPhotos && (
          <button type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-400 flex flex-col items-center justify-center text-gray-400 hover:border-amber-400 hover:text-amber-400 transition-colors">
            {uploading ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />}
            <span className="text-[10px] mt-1">{uploading ? 'Subiendo' : 'Añadir'}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
        onChange={e => handleFiles(e.target.files)} />
    </div>
  );
}

export default function ReviewFormPage() {
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState(null);
  const [couponExpiresAt, setCouponExpiresAt] = useState(null);
  const [couponCopied, setCouponCopied] = useState(false);

  // Form state
  const [shopRating, setShopRating] = useState(0);
  const [shopComment, setShopComment] = useState('');
  const [shopTitle, setShopTitle] = useState('');
  const [productReviews, setProductReviews] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/reviews/form/${token}`);
        if (!res.ok) {
          const data = await res.json();
          setError(data.detail || 'Enlace inválido o expirado');
          return;
        }
        const data = await res.json();
        setFormData(data);
        // Init product reviews
        setProductReviews((data.products || []).map(p => ({
          product_id: p.product_id,
          product_name: p.product_name,
          product_image_url: p.product_image_url,
          rating: 0,
          title: '',
          comment: '',
          photos: [],
        })));
      } catch (e) {
        setError('No se pudo cargar el formulario. Inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const updateProductReview = (idx, field, value) => {
    setProductReviews(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addPhoto = (idx, url) => {
    setProductReviews(rs => rs.map((r, i) => i === idx ? { ...r, photos: [...r.photos, url] } : r));
  };

  const removePhoto = (idx, pIdx) => {
    setProductReviews(rs => rs.map((r, i) => i === idx ? { ...r, photos: r.photos.filter((_, j) => j !== pIdx) } : r));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasShopRating = shopRating > 0;
    const validProducts = productReviews.filter(p => p.rating > 0);
    if (!hasShopRating && validProducts.length === 0) {
      alert('Debes dar al menos una valoración (tienda o producto)');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        language: formData.customer_language || 'es',
      };
      if (hasShopRating) {
        body.shop_rating = shopRating;
        body.shop_comment = shopComment;
        body.shop_title = shopTitle;
      }
      if (validProducts.length > 0) {
        body.product_reviews = validProducts.map(({ product_id, rating, title, comment, photos }) => ({
          product_id, rating, title, comment, photos,
        }));
      }

      const res = await fetch(`${BACKEND}/api/reviews/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.detail || 'Error al enviar');
        return;
      }
      const data = await res.json();
      if (data.coupon_code) {
        setCouponCode(data.coupon_code);
        setCouponExpiresAt(data.coupon_expires_at || null);
      }
      setSubmitted(true);
    } catch {
      alert('Error de conexión. Por favor, inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const pc = formData?.primary_color || '#6366f1';

  // Loading state
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader size={32} className="animate-spin text-gray-400" />
    </div>
  );

  // Error state (expired / invalid / already done)
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <AlertCircle size={48} className="mx-auto mb-4 text-amber-400" />
        <h2 className="text-gray-800 font-bold text-xl mb-2">Enlace no disponible</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  // Success state
  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-md w-full text-center">
        {/* Store header — same style as the form header */}
        <div className="py-7 px-6" style={{ backgroundColor: pc }}>
          {formData?.org_logo && (
            <img
              src={formData.org_logo}
              alt={formData.org_name}
              data-testid="success-org-logo"
              className="h-12 object-contain mx-auto mb-2"
            />
          )}
          {formData?.org_name && (
            <p data-testid="success-org-name" className="text-white font-semibold text-base">{formData.org_name}</p>
          )}
        </div>

        <div className="p-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: `${pc}18` }}>
            <CheckCircle size={40} style={{ color: pc }} />
          </div>
          <h2 className="text-gray-800 font-bold text-2xl mb-3">¡Gracias por tu opinión!</h2>
          <p className="text-gray-500 text-base">Tu valoración ha sido recibida. Nos ayuda mucho a mejorar nuestro servicio.</p>
          <div className="mt-6 flex justify-center gap-1">
            {[1,2,3,4,5].map(n => (
              <svg key={n} width={28} height={28} viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            ))}
          </div>

          {/* Coupon reward */}
          {couponCode && (
            <div className="mt-8 rounded-2xl border-2 border-dashed p-6" style={{ borderColor: pc }}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Tag size={18} style={{ color: pc }} />
                <p className="font-semibold text-gray-800 text-sm">¡Aquí tienes tu descuento!</p>
              </div>
              <p className="text-gray-500 text-xs mb-4">Como agradecimiento por tu opinión, te regalamos este cupón exclusivo:</p>

              {/* Code display */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="font-mono font-extrabold text-2xl tracking-widest px-5 py-3 rounded-xl"
                  style={{ backgroundColor: `${pc}15`, color: pc, letterSpacing: '0.25em' }}>
                  {couponCode}
                </div>
                <button
                  data-testid="coupon-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(couponCode).then(() => {
                      setCouponCopied(true);
                      setTimeout(() => setCouponCopied(false), 2000);
                    });
                  }}
                  className="p-2.5 rounded-xl border transition-all"
                  style={{
                    borderColor: couponCopied ? '#22c55e' : `${pc}40`,
                    color: couponCopied ? '#22c55e' : pc,
                    backgroundColor: couponCopied ? '#f0fdf4' : `${pc}08`,
                  }}
                  title="Copiar código"
                >
                  {couponCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>

              {couponExpiresAt && (
                <p className="text-gray-400 text-xs">Válido hasta: <span className="font-medium text-gray-600">{couponExpiresAt}</span></p>
              )}
              <p className="text-gray-400 text-xs mt-1">Introdúcelo al finalizar tu próxima compra</p>
            </div>
          )}
        </div>{/* end p-8 */}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Store header */}
      <div className="text-white py-8 px-4 text-center" style={{ backgroundColor: pc }}>
        {formData.org_logo && (
          <img src={formData.org_logo} alt={formData.org_name} className="h-12 object-contain mx-auto mb-3" />
        )}
        <h1 className="font-bold text-2xl">{formData.org_name}</h1>
        <p className="text-white/80 text-sm mt-1">Cuéntanos tu experiencia</p>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4">
        {/* Greeting card */}
        <div className="bg-white rounded-2xl shadow-md px-6 py-5 mb-5">
          <p className="text-gray-700 font-semibold">
            Hola {formData.customer_name ? formData.customer_name.split(' ')[0] : 'cliente'},
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Gracias por tu compra{formData.order_id ? ` (pedido #${formData.order_id})` : ''}. Tu opinión nos ayuda a seguir mejorando.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Shop review */}
          <div className="bg-white rounded-2xl shadow-md px-6 py-5">
            <h3 className="text-gray-800 font-bold text-base mb-4">Valoración general de la tienda</h3>
            <StarPicker value={shopRating} onChange={setShopRating} />
            {shopRating > 0 && (
              <div className="mt-4 space-y-3">
                <input type="text" placeholder="Título (opcional)"
                  value={shopTitle} onChange={e => setShopTitle(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': pc }} />
                <textarea placeholder="¿Cómo fue tu experiencia?" rows={3}
                  value={shopComment} onChange={e => setShopComment(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 text-sm resize-none focus:outline-none focus:ring-2 focus:border-transparent" />
              </div>
            )}
          </div>

          {/* Per-product reviews — only in shop_and_products mode */}
          {formData.review_mode !== 'shop_only' && productReviews.map((pr, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-md px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                {pr.product_image_url && (
                  <img src={pr.product_image_url} alt={pr.product_name}
                    className="w-14 h-14 object-cover rounded-xl border border-gray-100" />
                )}
                <div className="min-w-0">
                  <h4 className="text-gray-800 font-semibold text-sm leading-tight truncate">{pr.product_name}</h4>
                  <p className="text-gray-400 text-xs mt-0.5">¿Cómo valoras este producto?</p>
                </div>
              </div>
              <StarPicker value={pr.rating} onChange={v => updateProductReview(idx, 'rating', v)} />
              {pr.rating > 0 && (
                <div className="mt-4 space-y-3">
                  <input type="text" placeholder="Título de tu opinión (opcional)"
                    value={pr.title} onChange={e => updateProductReview(idx, 'title', e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:border-transparent" />
                  <textarea placeholder="¿Qué te pareció el producto?" rows={3}
                    value={pr.comment} onChange={e => updateProductReview(idx, 'comment', e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 text-sm resize-none focus:outline-none focus:ring-2 focus:border-transparent" />
                  <div>
                    <p className="text-gray-500 text-xs mb-2 flex items-center gap-1">
                      <Upload size={12} /> Añadir fotos (opcional, máx. 5)
                    </p>
                    <PhotoUpload
                      token={token}
                      photos={pr.photos}
                      onPhotoAdded={url => addPhoto(idx, url)}
                      onPhotoRemoved={pIdx => removePhoto(idx, pIdx)}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Submit */}
          <button type="submit" disabled={submitting}
            className="w-full text-white font-bold py-4 rounded-2xl text-base shadow-lg transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-60"
            style={{ backgroundColor: pc }}>
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader size={18} className="animate-spin" /> Enviando...
              </span>
            ) : 'Enviar mi opinión →'}
          </button>

          <p className="text-center text-gray-400 text-xs pb-4">
            Tu opinión puede ser moderada antes de publicarse. Powered by <strong>Sera</strong>.
          </p>
        </form>
      </div>
    </div>
  );
}
